import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Pencil, Trash2, Calendar, MapPin } from "lucide-react";
import { toast } from "sonner";
import { CATEGORY_BADGE } from "@/lib/categoryColors";
import { formatDateRangeShort, formatTimeRange } from "@/lib/dateFormat";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const STATUS_CONFIG = {
  pending: { label: "En attente", className: "bg-[#FEF3C7] text-[#92400E]" },
  validated: { label: "Validé", className: "bg-[#D1FAE5] text-[#065F46]" },
  refused: { label: "Refusé", className: "bg-[#FFE4E6] text-[#9F1239]" },
};

export default function PartnerDashboard() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/me/events");
      setEvents(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onDelete = async (id) => {
    try {
      await api.delete(`/events/${id}`);
      toast.success("Événement supprimé");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const stats = {
    pending: events.filter((e) => e.status === "pending").length,
    validated: events.filter((e) => e.status === "validated").length,
    refused: events.filter((e) => e.status === "refused").length,
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 fade-in">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-wider text-sage font-semibold mb-1">Espace Partenaire</div>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl text-ink">
            Bonjour {user?.name?.split(" ")[0]} 👋
          </h1>
          <p className="text-muted_text mt-1">Gérez vos événements et leur publication.</p>
        </div>
        <Link to="/partner/new">
          <Button className="rounded-xl bg-sage hover:bg-sage-dark text-white h-11 px-5" data-testid="new-event-button">
            <Plus className="w-4 h-4 mr-1.5" /> Nouvel événement
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-8">
        {[
          { label: "En attente", value: stats.pending, cls: "text-[#92400E]" },
          { label: "Validés", value: stats.validated, cls: "text-[#065F46]" },
          { label: "Refusés", value: stats.refused, cls: "text-[#9F1239]" },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-2xl border border-[#F0E0CC] p-4 sm:p-5">
            <div className="text-xs uppercase tracking-wider text-muted_text font-semibold">{s.label}</div>
            <div className={`text-2xl sm:text-3xl font-heading font-bold mt-1 ${s.cls}`}>{s.value}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-[#F0E0CC] overflow-hidden">
        <div className="p-5 sm:p-6 border-b border-[#F0E0CC]">
          <h2 className="font-heading font-semibold text-xl text-ink">Mes événements</h2>
        </div>

        {loading ? (
          <div className="p-10 text-center text-muted_text">Chargement…</div>
        ) : events.length === 0 ? (
          <div className="p-10 sm:p-16 text-center" data-testid="empty-partner-events">
            <h3 className="font-heading font-semibold text-lg text-ink">Aucun événement</h3>
            <p className="text-sm text-muted_text mt-1 mb-4">Commencez par créer votre premier événement.</p>
            <Link to="/partner/new">
              <Button className="rounded-xl bg-sage hover:bg-sage-dark text-white">
                <Plus className="w-4 h-4 mr-1.5" /> Créer un événement
              </Button>
            </Link>
          </div>
        ) : (
          <ul className="divide-y divide-[#F0E0CC]">
            {events.map((e) => {
              const cfg = STATUS_CONFIG[e.status] || STATUS_CONFIG.pending;
              return (
                <li key={e.id} className="p-4 sm:p-5 flex flex-col sm:flex-row gap-4 hover:bg-[#FEFCE8]/50" data-testid="partner-event-row">
                  <img
                    src={e.image_url} alt={e.title}
                    className="w-full sm:w-32 h-32 sm:h-20 object-cover rounded-xl bg-[#F0E0CC]"
                    onError={(ev) => { ev.currentTarget.src = "https://images.unsplash.com/photo-1517457373958-b7bdd4587205?w=300&q=80"; }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-2">
                      <h3 className="font-heading font-semibold text-ink flex-1 min-w-0 truncate">{e.title}</h3>
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.className}`}>
                        {cfg.label}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted_text mt-1">
                      <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {formatDateRangeShort(e.date, e.end_date)} • {formatTimeRange(e.time, e.end_time)}</span>
                      <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {e.commune}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${CATEGORY_BADGE[e.category] || "bg-[#FEF3C7] text-sage"}`}>{e.category}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 sm:self-center">
                    <Link to={`/partner/edit/${e.id}`}>
                      <Button variant="outline" size="sm" className="rounded-xl border-[#F0E0CC]" data-testid="edit-event-button">
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                    </Link>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="sm" className="rounded-xl border-[#F0E0CC] text-red-600 hover:text-red-700" data-testid="delete-event-button">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Supprimer cet événement ?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Cette action est irréversible. L'événement "{e.title}" sera définitivement supprimé.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Annuler</AlertDialogCancel>
                          <AlertDialogAction onClick={() => onDelete(e.id)} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-button">
                            Supprimer
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
